/**
 * 参考图数量配额校验：画布连线结构 vs 模型模式能力。
 *
 * 计数口径与 aggregateUpstream 一致：图片节点 1 张、图片组逐张计、视频封面 1 张，
 * 按连线输入顺序聚合；未出图的空占位不计入。
 * 能力未加载（模型未取到 / 模式能力缺失）时乐观放行，由各生成器与后端校验兜底。
 * 返回 null 表示通过，否则返回用户可读的置灰 / 报错文案。
 */

/** 图片模型的能力来源：ModelConfig.modeCapabilities 的形状（可选，未加载为 undefined） */
interface ImageQuotaSource {
  img2img?: { referenceImageMaxCount: number | null } | null;
  inpaint?: { referenceImageMaxCount: number | null } | null;
}

/** 图片生成：文生图不消费参考图不校验；图生图按通道上限；局部重绘恒 1 张 */
export function validateImageReferenceQuota(
  mode: string | undefined,
  count: number,
  caps: ImageQuotaSource | undefined | null
): string | null {
  if (mode === 'img2img') {
    const max = caps?.img2img?.referenceImageMaxCount;
    if (typeof max === 'number' && count > max) {
      return `参考图超出上限：最多 ${max} 张，当前 ${count} 张，请减少连线`;
    }
    return null;
  }
  if (mode === 'inpaint') {
    // 后端能力恒为 1；能力缺失时按 1 兜底，与提交侧语义一致
    const max = typeof caps?.inpaint?.referenceImageMaxCount === 'number' ? caps.inpaint!.referenceImageMaxCount! : 1;
    if (count !== max) {
      return `局部重绘需要恰好 ${max} 张参考图（当前 ${count} 张），请调整连线`;
    }
    return null;
  }
  return null;
}

/** 视频模式的能力来源：getModelCapabilitiesForMode 结果中与参考图相关的字段 */
interface VideoQuotaSource {
  available: boolean;
  maxReferenceImageCount: number;
  allowedReferenceImageCounts?: number[];
}

/** 视频生成：文生视频不校验；图生视频按上限与离散集合；首尾帧恒恰好 2 张 */
export function validateVideoReferenceQuota(
  mode: string | undefined,
  count: number,
  caps: VideoQuotaSource | undefined
): string | null {
  // 能力未加载（模型未取到 / 模式未声明）时乐观放行
  if (!caps?.available) return null;

  if (mode === 'img2video') {
    if (count < 1) {
      return '图生视频需要至少 1 张参考图（当前 0 张），请连接图片';
    }
    if (count > caps.maxReferenceImageCount) {
      return `图生视频最多支持 ${caps.maxReferenceImageCount} 张参考图（当前 ${count} 张），请减少连线`;
    }
    if (caps.allowedReferenceImageCounts && !caps.allowedReferenceImageCounts.includes(count)) {
      return `该模式仅支持 ${caps.allowedReferenceImageCounts.join(' / ')} 张参考图（当前 ${count} 张）`;
    }
    return null;
  }
  if (mode === 'first_last_frame') {
    if (count !== 2) {
      return `首尾帧模式需要恰好 2 张参考图：第一张作首帧、第二张作尾帧（当前 ${count} 张），请调整连线`;
    }
    return null;
  }
  return null;
}
